/**
 * TextInputModal component for collecting text input via a styled modal
 * Provides a consistent modal interface for name/text input prompts
 * Follows the same portal-based pattern as ConfirmDeleteModal
 */

import { Loader2, X } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export interface TextInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (value: string) => void;
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  maxLength?: number;
  required?: boolean;
}

// ============================================================================
// MODAL OVERLAY COMPONENT
// ============================================================================

function ModalOverlay({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClick();
        }
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function TextInputModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  label,
  placeholder,
  defaultValue = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  maxLength,
  required = false,
}: TextInputModalProps) {
  const [value, setValue] = React.useState(defaultValue);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Reset input value when modal opens/closes or defaultValue changes
  React.useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      // Auto-focus the input after a short delay for animation
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isOpen, defaultValue]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  const isConfirmDisabled = isLoading || (required && !value.trim());

  const handleConfirm = () => {
    if (isConfirmDisabled) return;
    onConfirm(value.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isConfirmDisabled) {
      e.preventDefault();
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="text-input-modal-title"
        aria-describedby={description ? 'text-input-modal-description' : undefined}
        tabIndex={-1}
        className={cn(
          'w-full max-w-md bg-surface rounded-xl shadow-xl border border-border-subtle',
          'animate-in zoom-in-95 slide-in-from-bottom-2 duration-200',
          'focus:outline-none'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 pb-0">
          <div className="flex-1 min-w-0">
            <h2
              id="text-input-modal-title"
              className="text-lg font-semibold text-primary leading-tight"
            >
              {title}
            </h2>
            {description && (
              <p
                id="text-input-modal-description"
                className="mt-2 text-sm text-secondary leading-relaxed"
              >
                {description}
              </p>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isLoading}
            className={cn(
              'flex-shrink-0 p-1 rounded-md text-muted hover:text-primary hover:bg-surface-subtle transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50',
              'disabled:opacity-50 disabled:pointer-events-none'
            )}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Input */}
        <div className="px-6 py-4">
          <Input
            ref={inputRef}
            label={label}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={isLoading}
          />
          {maxLength && (
            <p className="mt-1 text-xs text-muted text-right">
              {value.length}/{maxLength}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            leftIcon={
              isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : undefined
            }
          >
            {isLoading ? 'Processing...' : confirmText}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );

  return createPortal(modalContent, document.body);
}
